import { initializeApp } from 'firebase/app'
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, signOut, GoogleAuthProvider, signInWithPopup, type User } from 'firebase/auth'
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import type { OnboardingData } from '../types/onboarding'
import { getAttribution, getFbp, getFbc } from './attribution'
import { getVariant } from './variant'
import { funnelKidneyProfile } from './funnelProfile'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyC9lSvdpQKR1XEsDKQ9mc6EfjaVjkZYreU',
  authDomain: 'kidneyrecipes.firebaseapp.com',
  projectId: 'kidneyrecipes',
  storageBucket: 'kidneyrecipes.firebasestorage.app',
  messagingSenderId: '810820724974',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:810820724974:web:4f3cd0fdb33b892f83abff',
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)

export async function createAccount(email: string, password: string) {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password)
  return userCredential.user
}

export async function signIn(email: string, password: string) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password)
  return userCredential.user
}

export async function resetPassword(email: string) {
  await sendPasswordResetEmail(auth, email)
}

/**
 * Google sign-in for buyers whose app account uses Google (no password exists,
 * so the email/password form is a dead end — the purchase would stay orphaned).
 */
export async function signInWithGoogle(emailHint?: string) {
  const provider = new GoogleAuthProvider()
  if (emailHint) provider.setCustomParameters({ login_hint: emailHint })
  const userCredential = await signInWithPopup(auth, provider)
  return userCredential.user
}

/**
 * Has this account proven it owns its email? Mirrors the server's
 * `isTrustedIdentity` (functions/src/paddlePending.ts): a verified email, or a
 * Google / Apple sign-in. Only such an account can claim a parked web purchase.
 */
export function isTrustedUser(user: User): boolean {
  if (user.emailVerified) return true
  return user.providerData.some((p) => p.providerId === 'google.com' || p.providerId === 'apple.com')
}

/** The verification link returns to the funnel, which resumes on the account step. */
export async function sendVerification(user: User) {
  await sendEmailVerification(user, { url: `${window.location.origin}/?verified=1`, handleCodeInApp: false })
}

export async function signOutUser() {
  await signOut(auth)
}

/** True when a web purchase is already linked to this profile (e.g. by the server's profile trigger). */
export async function hasLinkedWebPurchase(uid: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'kpal_users', uid))
    return !!snap.data()?.webFunnelData?.paddleTransactionId
  } catch {
    return false
  }
}

/**
 * Save user onboarding data to Firestore in the EXACT format the KidneyPal app expects.
 *
 * This mirrors FirestoreService.createUserProfile() in services/firebase/firestore.ts
 * which is called by completion.tsx. The app writes ONLY these fields:
 *   - notificationsEnabled, theme, notificationPreferences, isPremium (defaults)
 *   - ...data (id, kidneyProfile, notificationsEnabled, isPremium)
 *   - createdAt, updatedAt (serverTimestamp)
 *
 * The app does NOT write dailyScans, streakData, or knownTriggers on profile creation.
 * Those are created lazily by the app when needed. Adding them here with incomplete
 * data causes crashes (e.g. missing streakData.milestones).
 *
 * We add webFunnelData as the only extra field (for Paddle subscription transfer).
 */
export async function saveUserOnboardingData(uid: string, onboardingData: OnboardingData) {
  // S1: calculatedLimits only, like an account made in the app (funnelProfile.ts).
  const kidneyProfile = funnelKidneyProfile(onboardingData)

  // Exact same structure as FirestoreService.createUserProfile() defaults + spread
  const userData: Record<string, any> = {
    // Defaults (same as firestore.ts lines 32-45)
    notificationsEnabled: true,
    theme: 'system',
    notificationPreferences: {
      breakfastReminder: true,
      lunchReminder: true,
      dinnerReminder: true,
      streakReminders: true,
      streakMilestones: true,
    },
    isPremium: false,

    // User data (same as completion.tsx lines 84-100)
    id: uid,
    kidneyProfile,

    // Timestamps. createdAt is create-only: Firestore rules reject any update
    // that changes createdAt/id, so it is added below only when no profile
    // exists yet (an app user who lands on the funnel keeps their original).
    updatedAt: serverTimestamp(),

    // ONLY extra field: web funnel metadata for Paddle subscription transfer
    webFunnelData: {
      source: 'web-funnel-v1',
      variant: onboardingData.funnelVariant || getVariant(),
      createdAt: new Date().toISOString(),
      paddleTransactionId: onboardingData.paddleTransactionId || null,
      email: onboardingData.email || null,
      primaryGoal: onboardingData.primaryGoal || null,
      dietKnowledge: onboardingData.dietKnowledge || null,
      featurePriority: onboardingData.featurePriority || null,
      attribution: (() => {
        const attr = getAttribution()
        if (!attr) return null
        return {
          gclid: attr.gclid || null,
          gbraid: attr.gbraid || null,
          wbraid: attr.wbraid || null,
          fbclid: attr.fbclid || null,
          utm_source: attr.utm_source || null,
          utm_medium: attr.utm_medium || null,
          utm_campaign: attr.utm_campaign || null,
          utm_content: attr.utm_content || null,
          utm_term: attr.utm_term || null,
          landingPage: attr.landingPage || null,
        }
      })(),
      // Meta CAPI enrichment — the account-transfer trigger (paddleWebhook.ts)
      // prefers these stored browser ids when it queues the Meta outbox event
      meta: {
        fbp: getFbp() || null,
        fbc: getFbc() || null,
        purchaseEventId: onboardingData.metaPurchaseEventId || null,
        planId: onboardingData.purchasePlanId || null,
        priceUsd: onboardingData.purchasePriceUsd ?? null,
      },
    },
  }

  const ref = doc(db, 'kpal_users', uid)
  let exists = false
  try {
    exists = (await getDoc(ref)).exists()
  } catch {
    // Unreadable (offline) — assume new; if a profile does exist the rules
    // reject the createdAt change and the write fails loudly rather than clobbering.
  }
  const payload = exists ? userData : { ...userData, createdAt: serverTimestamp() }
  await setDoc(ref, payload, { merge: true })
}
